import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Input, Row, Col, Select, Spin, message, DatePicker, Radio, Segmented } from 'antd';
import PipeMaterialSelect from '../../../../components/modals/PipeMaterialSelect';
import type { QaqcPipeWeldingdataResult, QaqcPipeWeldingdataLog } from '../../../../types';
import { updateData } from '../../../../services/api';
import dayjs from 'dayjs';
import { useGetQaqcPipeWeldingdataLog } from '../../../../hooks/genericHooks';
import Button from '../../../../components/common/Button';

interface Props {
  open: boolean;
  onClose: () => void;
  initialValues: QaqcPipeWeldingdataResult | null;
  onUpdated?: () => void; // callback to refresh parent
}
// Result control will use Radio buttons; no options array needed

// Single knob to control widths (in px) of EDITABLE fields only
const CONTROL_WIDTH = 120;
// Width control for read-only component fields
const COMPONENT_FIELD_WIDTH = 420;

const QaqcPipeWeldingDataResultForm: React.FC<Props> = ({ open, onClose, initialValues, onUpdated }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [pmInitialItemcode, setPmInitialItemcode] = useState<string | undefined>(undefined);
  const [pmTargetField, setPmTargetField] = useState<'subitemcode1' | 'subitemcode2' | null>(null);
  // Fetch welding data log to display contextual fields for selected weldingdata_id
  const { data: logData } = useGetQaqcPipeWeldingdataLog() as any;
  const logList: QaqcPipeWeldingdataLog[] = useMemo(() => Array.isArray(logData) ? logData : (logData?.data ?? []), [logData]);
  const logRec = useMemo(() => {
    const wd = initialValues?.weldingdata_id;
    if (!wd) return null;
    return (logList || []).find(r => String(r.weldingdata_id) === String(wd)) || null;
  }, [logList, initialValues]);
  useEffect(() => {
    if (open) {
      if (initialValues) {
        const iv = initialValues as any;
        form.setFieldsValue({
          weldingdata_id: iv.weldingdata_id,
          weld_no_rev: iv.weld_no_rev,
          component_name1: iv.component_name1,
          component_name2: iv.component_name2,
          subitemcode1: iv.subitemcode1,
          subitemcode2: iv.subitemcode2,
          bc_inspector: iv.bc_inspector,
          vc_inspector: iv.vc_inspector,
          pmi_inspector: iv.pmi_inspector,
          pt_inspector: iv.pt_inspector,
          rt_inspector: iv.rt_inspector,
          mt_inspector: iv.mt_inspector,
          ut_inspector: iv.ut_inspector,
          bc_report_no: iv.bc_report_no,
          vc_report_no: iv.vc_report_no,
          pwht_report_no: iv.pwht_report_no,
          pmi_report_no: iv.pmi_report_no,
          pt_report_no: iv.pt_report_no,
          rt_report_no: iv.rt_report_no,
          mt_report_no: iv.mt_report_no,
          ut_report_no: iv.ut_report_no,
          flw_welder: iv.flw_welder,
          fw_welder: iv.fw_welder,
          pwht_hardness: iv.pwht_hardness,
          pwht_inspector: iv.pwht_inspector,
          pt_result: iv.pt_result,
          rt_result: iv.rt_result,
          mt_result: iv.mt_result,
          ut_result: iv.ut_result,
          // Date fields as dayjs
          flw_date: iv.flw_date ? dayjs(iv.flw_date) : null,
          fw_date: iv.fw_date ? dayjs(iv.fw_date) : null,
          bc_date: iv.bc_date ? dayjs(iv.bc_date) : null,
          vc_date: iv.vc_date ? dayjs(iv.vc_date) : null,
          pmi_date: iv.pmi_date ? dayjs(iv.pmi_date) : null,
          pt_date: iv.pt_date ? dayjs(iv.pt_date) : null,
          rt_date: iv.rt_date ? dayjs(iv.rt_date) : null,
          mt_date: iv.mt_date ? dayjs(iv.mt_date) : null,
          ut_date: iv.ut_date ? dayjs(iv.ut_date) : null,
          pwht_date: iv.pwht_date ? dayjs(iv.pwht_date) : null,
          ht_rev_date: iv.ht_rev_date ? dayjs(iv.ht_rev_date) : null,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleFinish = async (values: any) => {
    try {
      setSubmitting(true);
      const id = (initialValues as any)?.id;
      if (!id) {
        message.error('Missing record id');
        return;
      }
      // Normalize result fields to boolean or null
      const normalize = (v: any): boolean | null => (v === true ? true : v === false ? false : null);
      // Convert dayjs to YYYY-MM-DD strings
      const toDateStr = (d: any) => (d && dayjs.isDayjs(d) ? d.format('YYYY-MM-DD') : (typeof d === 'string' ? d : null));

      const payload: Partial<QaqcPipeWeldingdataResult> = {
        bc_inspector: values.bc_inspector ?? null,
        vc_inspector: values.vc_inspector ?? null,
        pmi_inspector: values.pmi_inspector ?? null,
        pt_inspector: values.pt_inspector ?? null,
        rt_inspector: values.rt_inspector ?? null,
        mt_inspector: values.mt_inspector ?? null,
        ut_inspector: values.ut_inspector ?? null,
        bc_report_no: values.bc_report_no ?? null,
        vc_report_no: values.vc_report_no ?? null,
        pwht_report_no: values.pwht_report_no ?? null,
        pmi_report_no: values.pmi_report_no ?? null,
        pt_report_no: values.pt_report_no ?? null,
        rt_report_no: values.rt_report_no ?? null,
        mt_report_no: values.mt_report_no ?? null,
        ut_report_no: values.ut_report_no ?? null,
        subitemcode1: values.subitemcode1 ?? null,
        subitemcode2: values.subitemcode2 ?? null,
        flw_welder: values.flw_welder ?? null,
        fw_welder: values.fw_welder ?? null,
        pwht_hardness: values.pwht_hardness ?? null,
        pwht_inspector: values.pwht_inspector ?? null,
        pt_result: normalize(values.pt_result),
        rt_result: normalize(values.rt_result),
        mt_result: normalize(values.mt_result),
        ut_result: normalize(values.ut_result),
        flw_date: toDateStr(values.flw_date),
        fw_date: toDateStr(values.fw_date),
        bc_date: toDateStr(values.bc_date),
        vc_date: toDateStr(values.vc_date),
        pmi_date: toDateStr(values.pmi_date),
        pt_date: toDateStr(values.pt_date),
        rt_date: toDateStr(values.rt_date),
        mt_date: toDateStr(values.mt_date),
        ut_date: toDateStr(values.ut_date),
        pwht_date: toDateStr(values.pwht_date),
        ht_rev_date: toDateStr(values.ht_rev_date),
      } as any;

      await updateData('qaqc_pipe_weldingdata_result', id, payload);
      message.success('Record updated');
      onUpdated?.();
      onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={initialValues?.id ? `Edit Result • ${initialValues.weldingdata_id}` : 'Edit Result'}
      width={720}
      extra={
        <div>
          <Button onClick={onClose} style={{ marginRight: 8 }}>Cancel</Button>
          <Button action="edit" type="primary" onClick={() => form.submit()} loading={submitting}>Save</Button>
        </div>
      }
      onClose={onClose}
      open={open}
      destroyOnClose
      zIndex={3000}
    >
      {/* Local styles for Segmented selected background colors */}
      <style>{`
        /* Selected chip backgrounds */
        .seg-pass .ant-segmented-item-selected {
          background-color: #1677ff !important;
          border-color: #1677ff !important;
        }
        .seg-fail .ant-segmented-item-selected {
          background-color: #ff4d4f !important;
          border-color: #ff4d4f !important;
        }
        /* Ensure selected label text is visible (white) */
        .seg-pass .ant-segmented-item-selected .ant-segmented-item-label,
        .seg-pass .ant-segmented-item-selected .ant-segmented-item-label span,
        .seg-fail .ant-segmented-item-selected .ant-segmented-item-label,
        .seg-fail .ant-segmented-item-selected .ant-segmented-item-label span {
          color: #fff !important;
        }
      `}</style>
      <Spin spinning={false}>
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          onFinish={handleFinish}
        >
          {/* Compact item layout helpers */}
          {(() => null)()}
           <Row gutter={1}>
            <Col span={5}><Form.Item label="Fluid" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.fluid ?? ''} style={{ width: '60px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
            <Col span={5}><Form.Item label="Serv.Class" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.serviceclass_name ?? ''} style={{ width: '60px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
            <Col span={5}><Form.Item label="Section" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.section ?? ''} style={{ width: '70px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
            <Col span={9}><Form.Item label="Line" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.line_no ?? ''} style={{ width: '120px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
          </Row>
          <Row gutter={2}>
            <Col span={5}><Form.Item label="Spool" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.spool_id ?? ''} style={{ width: '60px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
            <Col span={5}><Form.Item label="Rev.No" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
              <Input disabled value={logRec?.revision_no ?? ''} style={{ width: '60px', color: 'rgba(0,0,0,0.88)' }} />
            </Form.Item></Col>
            <Col span={5}>
              <Form.Item label="Size" style={{ marginBottom: 6 }} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
                <Input disabled value={logRec?.pipesize_name ?? ''} style={{ width: '70px', color: 'rgba(0,0,0,0.88)' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={0}>
            <Col span={12}><Form.Item name="weldingdata_id" label="Welding Data ID" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><Input disabled style={{ color: 'rgba(0,0,0,0.88)' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="weld_no_rev" label="Rev" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: '060px' }}><Input disabled style={{ color: 'rgba(0,0,0,0.88)' }} /></Form.Item></Col>
          </Row>

          <Row gutter={8}>
            <Col span={24}><Form.Item name="component_name1" label="Component 1" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><Input disabled style={{ color: 'rgba(0,0,0,0.88)' }} /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={24}><Form.Item name="component_name2" label="Component 2" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><Input disabled style={{ color: 'rgba(0,0,0,0.88)' }} /></Form.Item></Col>
          </Row>
          {/* Context from welding data log */}

          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="subitemcode1" label="Sub Item Code 1" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}>
                <Input
                  style={{ width: CONTROL_WIDTH }}
                  readOnly={!!form.getFieldValue('subitemcode1')}
                  onDoubleClick={() => {
                    setPmTargetField('subitemcode1');
                    setPmInitialItemcode(form.getFieldValue('subitemcode1'));
                    setPmOpen(true);
                  }}
                  onKeyDown={(e) => {
                    const hasVal = !!form.getFieldValue('subitemcode1');
                    if (e.key === 'Backspace' && hasVal) {
                      e.preventDefault();
                      form.setFieldsValue({ subitemcode1: null });
                      // ensure next open has no initial filter
                      setPmInitialItemcode(undefined);
                      return;
                    }
                    // For any other typing, open modal selector
                    e.preventDefault();
                    setPmTargetField('subitemcode1');
                    setPmInitialItemcode(form.getFieldValue('subitemcode1'));
                    setPmOpen(true);
                  }}
                  onClick={() => {
                    if (form.getFieldValue('subitemcode1')) {
                      setPmTargetField('subitemcode1');
                      setPmInitialItemcode(form.getFieldValue('subitemcode1'));
                      setPmOpen(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="subitemcode2" label="Sub Item Code 2" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}>
                <Input
                  style={{ width: CONTROL_WIDTH }}
                  readOnly={!!form.getFieldValue('subitemcode2')}
                  onDoubleClick={() => {
                    setPmTargetField('subitemcode2');
                    setPmInitialItemcode(form.getFieldValue('subitemcode2'));
                    setPmOpen(true);
                  }}
                  onKeyDown={(e) => {
                    const hasVal = !!form.getFieldValue('subitemcode2');
                    if (e.key === 'Backspace' && hasVal) {
                      e.preventDefault();
                      form.setFieldsValue({ subitemcode2: null });
                      // ensure next open has no initial filter
                      setPmInitialItemcode(undefined);
                      return;
                    }
                    // For any other typing, open modal selector
                    e.preventDefault();
                    setPmTargetField('subitemcode2');
                    setPmInitialItemcode(form.getFieldValue('subitemcode2'));
                    setPmOpen(true);
                  }}
                  onClick={() => {
                    if (form.getFieldValue('subitemcode2')) {
                      setPmTargetField('subitemcode2');
                      setPmInitialItemcode(form.getFieldValue('subitemcode2'));
                      setPmOpen(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* Welding operations */}
          <Row gutter={8}>
            <Col span={12}><Form.Item name="flw_date" label="FLW Date" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="flw_welder" label="FLW Welder" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="fw_date" label="FW Date" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="fw_welder" label="FW Welder" style={{ marginBottom: 6 }} labelCol={{ flex: '120px' }} wrapperCol={{ flex: 'auto' }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
          </Row>
          {/* Report matrix header */}
          <Row gutter={20} style={{ marginTop: 8 }}>
            <Col span={4}><div style={{ fontWeight: 600 }}>Inspection/Test</div></Col>
            <Col span={5}><div style={{ fontWeight: 600 }}>Report No</div></Col>
            <Col span={5}><div style={{ fontWeight: 600 }}>Report Date</div></Col>
            <Col span={5}><div style={{ fontWeight: 600 }}>Inspector</div></Col>
            <Col span={5}><div style={{ fontWeight: 600 }}>Result</div></Col>
          </Row>
          {/* BC row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2, textAlign: 'center' }}>BC</div></Col>
            <Col span={5}><Form.Item name="bc_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="bc_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="bc_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><div /></Col>
          </Row>
          {/* VC row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>VC</div></Col>
            <Col span={5}><Form.Item name="vc_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="vc_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="vc_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><div /></Col>
          </Row>
          {/* PMI row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>PMI</div></Col>
            <Col span={5}><Form.Item name="pmi_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pmi_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pmi_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><div /></Col>
          </Row>
          {/* PT row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>PT</div></Col>
            <Col span={5}><Form.Item name="pt_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pt_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pt_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}>
              <Form.Item name="pt_result" style={{ marginBottom: 6 }}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.pt_result !== cur.pt_result}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const v = getFieldValue('pt_result');
                    return (
                      <>
                        <div
                          onMouseDown={(e) => {
                            const el = e.target as HTMLElement;
                            const item = el.closest('.ant-segmented-item');
                            if (item && item.classList.contains('ant-segmented-item-selected')) {
                              e.preventDefault();
                              e.stopPropagation();
                              setFieldsValue({ pt_result: null });
                            }
                          }}
                        >
                          <Segmented
                            options={[
                              { label: <span style={{ color: v === true ? '#1677ff' : undefined }}>P</span>, value: true },
                              { label: <span style={{ color: v === false ? '#ff4d4f' : undefined }}>F</span>, value: false },
                              { label: <span>CLR</span>, value: 'CLR' },
                            ]}
                            value={v as any}
                            onChange={(val) => setFieldsValue({ pt_result: (val === 'CLR' ? null : (val as any)) })}
                            className={v === true ? 'seg-pass' : v === false ? 'seg-fail' : undefined}
                            style={{ width: CONTROL_WIDTH }}
                          />
                        </div>
                        
                      </>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          {/* RT row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>RT</div></Col>
            <Col span={5}><Form.Item name="rt_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="rt_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="rt_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}>
              <Form.Item name="rt_result" style={{ marginBottom: 6 }}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.rt_result !== cur.rt_result}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const v = getFieldValue('rt_result');
                    return (
                      <>
                        <div
                          onMouseDown={(e) => {
                            const el = e.target as HTMLElement;
                            const item = el.closest('.ant-segmented-item');
                            if (item && item.classList.contains('ant-segmented-item-selected')) {
                              e.preventDefault();
                              e.stopPropagation();
                              setFieldsValue({ rt_result: null });
                            }
                          }}
                        >
                          <Segmented
                            options={[
                              { label: <span style={{ color: v === true ? '#1677ff' : undefined }}>P</span>, value: true },
                              { label: <span style={{ color: v === false ? '#ff4d4f' : undefined }}>F</span>, value: false },
                              { label: <span>CLR</span>, value: 'CLR' },
                            ]}
                            value={v as any}
                            onChange={(val) => setFieldsValue({ rt_result: (val === 'CLR' ? null : (val as any)) })}
                            className={v === true ? 'seg-pass' : v === false ? 'seg-fail' : undefined}
                            style={{ width: CONTROL_WIDTH }}
                          />
                        </div>
                        
                      </>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          {/* MT row */}
          <Row gutter={8}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>MT</div></Col>
            <Col span={5}><Form.Item name="mt_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="mt_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="mt_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}>
              <Form.Item name="mt_result" style={{ marginBottom: 6 }}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.mt_result !== cur.mt_result}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const v = getFieldValue('mt_result');
                    return (
                      <>
                        <div
                          onMouseDown={(e) => {
                            const el = e.target as HTMLElement;
                            const item = el.closest('.ant-segmented-item');
                            if (item && item.classList.contains('ant-segmented-item-selected')) {
                              e.preventDefault();
                              e.stopPropagation();
                              setFieldsValue({ mt_result: null });
                            }
                          }}
                        >
                          <Segmented
                            options={[
                              { label: <span style={{ color: v === true ? '#1677ff' : undefined }}>P</span>, value: true },
                              { label: <span style={{ color: v === false ? '#ff4d4f' : undefined }}>F</span>, value: false },
                              { label: <span>CLR</span>, value: 'CLR' },
                            ]}
                            value={v as any}
                            onChange={(val) => setFieldsValue({ mt_result: (val === 'CLR' ? null : (val as any)) })}
                            className={v === true ? 'seg-pass' : v === false ? 'seg-fail' : undefined}
                            style={{ width: CONTROL_WIDTH }}
                          />
                        </div>
                        
                      </>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          {/* UT row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>UT</div></Col>
            <Col span={5}><Form.Item name="ut_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="ut_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="ut_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}>
              <Form.Item name="ut_result" style={{ marginBottom: 6 }}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.ut_result !== cur.ut_result}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const v = getFieldValue('ut_result');
                    return (
                      <>
                        <div
                          onMouseDown={(e) => {
                            const el = e.target as HTMLElement;
                            const item = el.closest('.ant-segmented-item');
                            if (item && item.classList.contains('ant-segmented-item-selected')) {
                              e.preventDefault();
                              e.stopPropagation();
                              setFieldsValue({ ut_result: null });
                            }
                          }}
                        >
                          <Segmented
                            options={[
                              { label: <span style={{ color: v === true ? '#1677ff' : undefined }}>P</span>, value: true },
                              { label: <span style={{ color: v === false ? '#ff4d4f' : undefined }}>F</span>, value: false },
                              { label: <span>CLR</span>, value: 'CLR' },
                            ]}
                            value={v as any}
                            onChange={(val) => setFieldsValue({ ut_result: (val === 'CLR' ? null : (val as any)) })}
                            className={v === true ? 'seg-pass' : v === false ? 'seg-fail' : undefined}
                            style={{ width: CONTROL_WIDTH }}
                          />
                        </div>
                        
                      </>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          {/* PWHT row */}
          <Row gutter={4}>
            <Col span={4}><div style={{ paddingTop: 2 , textAlign: 'center' }}>PWHT</div></Col>
            <Col span={5}><Form.Item name="pwht_report_no" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pwht_date" style={{ marginBottom: 6 }}><DatePicker style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><Form.Item name="pwht_inspector" style={{ marginBottom: 6 }}><Input style={{ width: CONTROL_WIDTH }} /></Form.Item></Col>
            <Col span={5}><div /></Col>
          </Row>

          {/* Other */}
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="ht_rev_date"
                label="HT Rev Date"
                style={{ marginBottom: 6 }}
                labelCol={{ flex: '120px' }}
                wrapperCol={{ flex: 'auto' }}
              >
                <DatePicker style={{ width: CONTROL_WIDTH }} />
              </Form.Item>
            </Col>
          </Row>

          {(() => null)()}
        </Form>
      </Spin>
      <PipeMaterialSelect
        open={pmOpen}
        mode="basic"
        initialItemcode={pmInitialItemcode}
        onCancel={() => setPmOpen(false)}
        onOk={(value) => {
          if (typeof value === 'string' && pmTargetField) {
            form.setFieldsValue({ [pmTargetField]: value });
          }
          setPmOpen(false);
        }}
      />
    </Drawer>
  );
};

export default QaqcPipeWeldingDataResultForm;
